import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SEOHead } from '../components/SEOHead';
import { Breadcrumbs } from '../components/seo/Breadcrumbs';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

export const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => { axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SEOHead title="Blog BTP – réglementation, agréation, assurances et marchés publics en Belgique" description="Articles pratiques et sourcés pour les professionnels du bâtiment : agréation des entrepreneurs, assurances obligatoires, TVA 6 %, marchés publics e-Procurement et TED, sous-traitance." path="/blog" />
      <section className="bg-[#1E3A8A] text-white"><div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs items={[{ label: 'Blog', path: '/blog' }]} />
        <h1 className="font-heading font-bold text-4xl sm:text-5xl mt-4 mb-3" data-testid="blog-title">Blog BuildConnect</h1>
        <p className="text-blue-100 text-base md:text-lg max-w-2xl">Réglementation belge, conseils de sous-traitance et actualités du secteur, rédigés pour les entrepreneurs.</p>
      </div></section>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-list">
        {posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}>
            <Card className="card-surface h-full"><CardContent className="p-6">
              <div className="flex flex-wrap gap-1 mb-2">{(p.tags || []).map((t) => <Badge key={t} className="badge-trade text-xs">{t}</Badge>)}</div>
              <h2 className="font-heading font-bold text-lg text-[#111827]">{p.title}</h2>
              <p className="text-sm text-[#4B5563] mt-2">{p.excerpt}</p>
              <p className="text-xs text-[#9CA3AF] mt-3 flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmt(p.published_at)}</p>
            </CardContent></Card>
          </Link>
        ))}
        {posts.length === 0 && <p className="text-[#6B7280]">Aucun article publié pour le moment.</p>}
      </div>
    </div>
  );
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  useEffect(() => { axios.get(`${API}/blog/${slug}`).then((r) => setPost(r.data)).catch(() => navigate('/blog')); }, [slug, navigate]);
  if (!post) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A8A]" /></div>;
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'Article', headline: post.title, description: post.meta_description || post.excerpt,
    datePublished: post.published_at, dateModified: post.updated_at, author: { '@type': 'Organization', name: 'BuildConnect' },
    publisher: { '@type': 'Organization', name: 'BuildConnect', url: 'https://buildconnect.be' }, mainEntityOfPage: `https://buildconnect.be/blog/${post.slug}`,
    citation: (post.sources || []).map((s) => s.url),
  };
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SEOHead title={post.title} description={post.meta_description || post.excerpt} path={`/blog/${post.slug}`} type="article" jsonLd={jsonLd} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs items={[{ label: 'Blog', path: '/blog' }, { label: post.title, path: `/blog/${post.slug}` }]} />
        <div className="flex flex-wrap gap-1 mt-4 mb-2">{(post.tags || []).map((t) => <Badge key={t} className="badge-trade text-xs">{t}</Badge>)}</div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-[#111827]" data-testid="blog-post-title">{post.title}</h1>
        <p className="text-[#4B5563] mt-3 text-base md:text-lg">{post.excerpt}</p>
        <p className="text-xs text-[#9CA3AF] mt-3 flex items-center gap-3"><span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmt(post.published_at)}</span><span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.reading_time} min de lecture</span></p>
        <div className="prose prose-slate max-w-none mt-8 prose-headings:font-heading prose-a:text-[#1E3A8A] prose-table:text-sm" data-testid="blog-post-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content_md}</ReactMarkdown>
        </div>
        {post.sources?.length > 0 && (
          <div className="mt-8 border-t pt-4 text-sm" data-testid="blog-post-sources">
            <p className="font-semibold mb-2">Sources</p>
            <ul className="space-y-1">{post.sources.map((s) => <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#1E3A8A] hover:underline"><ExternalLink className="w-3 h-3" />{s.name}</a></li>)}</ul>
          </div>
        )}
        {post.related?.length > 0 && (
          <div className="mt-8"><p className="font-heading font-bold mb-2">À lire aussi</p>
            <ul className="space-y-1 text-sm">{post.related.map((r) => <li key={r.id}><Link to={`/blog/${r.slug}`} className="text-[#1E3A8A] hover:underline">{r.title}</Link></li>)}</ul>
          </div>
        )}
        <p className="text-sm text-[#6B7280] mt-8">Besoin d'un sous-traitant ? Consultez <Link to="/contractors" className="text-[#1E3A8A] underline">l'annuaire</Link> ou <Link to="/dashboard/post-job" className="text-[#1E3A8A] underline">publiez un chantier</Link>. Questions réglementaires : <Link to="/faq" className="text-[#1E3A8A] underline">la FAQ</Link>.</p>
      </article>
    </div>
  );
};
